import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle, Bug, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Footer from '../components/Footer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import ContactSupport from '../components/ContactSupport';
import BugReportForm from '../components/BugReportForm';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';

const Help = () => {
  const supportOptions = [
    {
      icon: Mail,
      title: "Email Support",
      description: "support@ovalu.app",
      action: "email"
    },
    {
      icon: MessageCircle,
      title: "Live Chat",
      description: "Available 9 AM - 6 PM IST",
      action: "chat"
    },
    {
      icon: Bug,
      title: "Report a Bug",
      description: "Help us fix issues",
      action: "bug"
    }
  ];

  const faqs = [
    {
      question: "How does Ovalu predict my period?",
      answer: "Ovalu uses your logged period data to calculate average cycle length, then predicts future dates using the formula: Next Period = Last Period Start + Average Cycle Length."
    },
    {
      question: "How is ovulation calculated?",
      answer: "Ovulation is estimated at Cycle Length minus 14 days from the start of your last period. The fertile window spans ovulation ± 2 days."
    },
    {
      question: "Is my data secure?",
      answer: "Yes, absolutely. All your personal health data is encrypted and stored securely. We follow strict privacy protocols and never share your information with third parties without your consent."
    },
    {
      question: "Can I export my data?",
      answer: "Yes, you can export all your cycle data in CSV format from the Settings menu. This allows you to keep a backup or share with your healthcare provider."
    },
    {
      question: "How do I sync with wearable devices?",
      answer: "Go to Settings > Integrations and connect your wearable device. We support most popular fitness trackers including Apple Watch, Fitbit, and Garmin."
    },
    {
      question: "Can I track symptoms?",
      answer: "Yes! You can log various symptoms like mood, cramps, headaches, and more. Our AI uses this data to provide personalized insights."
    },
    {
      question: "How do expert consultations work?",
      answer: "Expert consultations connect you with certified healthcare professionals. Book a session through the app, and you'll receive personalized guidance via video call or chat."
    },
    {
      question: "What if I have irregular cycles?",
      answer: "Ovalu's AI is designed to adapt to irregular cycles. The more data you log, the better the predictions become. You can also manually adjust predictions."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F8] to-white">
      <Navbar />

      {/* Content */}
      <div className="pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-4xl">
          {/* Hero */}
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">Help & Support</h1>
            <p className="text-xl text-gray-600">
              We're here to help you with any questions or concerns
            </p>
          </div>

          {/* Support Options */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {supportOptions.map((option, index) => {
              const CardContent = (
                <div className="block cursor-pointer outline-none">
                  <Card className="p-6 border-pink-100 hover:border-[#FF6B9D] hover:shadow-lg transition-all duration-300 group h-full">
                    <div className="w-14 h-14 bg-pink-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-[#FF6B9D] transition-colors">
                      <option.icon className="w-7 h-7 text-[#FF6B9D] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{option.title}</h3>
                    <p className="text-gray-600 font-medium">{option.description}</p>
                  </Card>
                </div>
              );

              if (option.action === 'email') {
                return (
                  <ContactSupport key={index}>
                    {CardContent}
                  </ContactSupport>
                );
              }

              if (option.action === 'bug') {
                return (
                  <BugReportForm key={index}>
                    {CardContent}
                  </BugReportForm>
                );
              }

              if (option.action === 'chat') {
                return (
                  <div key={index} onClick={() => toast.info("Click the floating chat bubble on the bottom right to start!")}>
                    {CardContent}
                  </div>
                );
              }

              return <div key={index}>{CardContent}</div>;
            })}
          </div>

          {/* FAQ Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Frequently Asked Questions
            </h2>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`} 
                  className="border border-pink-100 rounded-2xl px-6 hover:border-[#FF6B9D] transition-colors"
                >
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#FF6B9D]">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Still Need Help */}
          <Card className="p-8 md:p-12 bg-gradient-to-br from-[#FF6B9D] to-[#FFB6C1] border-none text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Still need help?</h2>
            <p className="text-xl mb-6 text-white/90">
              Our support team is ready to assist you
            </p>
            <ContactSupport>
              <Button className="bg-white text-[#FF6B9D] hover:bg-gray-50 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all">
                Contact Support
              </Button>
            </ContactSupport>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Help;
